import React from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
} from "@mui/material";

// Options shape — each element may be:
//   - a primitive (string/number) — rendered as both value and label
//   - { value, name }                — value + label
//   - { value, name, description }   — adds a small subtitle under the label
//                                      in the dropdown panel (collapsed
//                                      select still shows label only)
//
// `helperText` (string OR (value) => string) renders below the dropdown.
// Useful for explaining the consequence of the current selection (e.g. DLT
// category implications).
const Select = ({
  id,
  sx,
  label,
  value,
  onChange,
  options,
  disabled = false,
  multiple = false,
  required = false,
  onClose,
  helperText,
  emptyText = "No options available",
}) => {
  // Map value -> short label so the collapsed select stays clean even when
  // the option panel renders rich {label + description} content.
  const labelByValue = options.reduce((acc, el) => {
    if (el && typeof el === "object") acc[el.value] = el.name ?? el.node ?? el.value;
    else acc[el] = el;
    return acc;
  }, {});

  const resolvedHelper =
    typeof helperText === "function" ? helperText(value) : helperText;

  return (
    <FormControl
      required={required}
      sx={sx}
      size="small"
      variant="standard"
      key={id}
    >
      <InputLabel
        id={`${id}-label`}
        shrink={multiple ? value.length > 0 : value === 0 ? true : !!value}
      >
        {label}
      </InputLabel>
      <MuiSelect
        labelId={`${id}-label`}
        multiple={multiple}
        id={id}
        value={value}
        label={label}
        onChange={onChange}
        disabled={disabled}
        renderValue={(selected) => {
          if (multiple) return selected.join(", ");
          return labelByValue[selected] ?? selected;
        }}
        required={required}
        {...(onClose ? { onClose } : {})}
      >
        {options.length === 0 ? (
          <MenuItem disabled sx={{ color: "text.secondary", fontStyle: "italic" }}>
            {emptyText}
          </MenuItem>
        ) : null}
        {options.map((el) => {
          const v = el?.value ?? el;
          const name = el?.name ?? el?.node ?? el;
          const description = el?.description;
          return (
            <MenuItem
              key={v}
              value={v}
              sx={
                description
                  ? {
                      alignItems: "flex-start",
                      flexDirection: "column",
                      whiteSpace: "normal",
                    }
                  : undefined
              }
            >
              {multiple ? <Checkbox checked={value.includes(v)} /> : null}
              {description ? (
                <>
                  <Box sx={{ fontWeight: 500 }}>{name}</Box>
                  <Box sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
                    {description}
                  </Box>
                </>
              ) : (
                name
              )}
            </MenuItem>
          );
        })}
      </MuiSelect>
      {resolvedHelper ? (
        <FormHelperText sx={{ mx: 0 }}>{resolvedHelper}</FormHelperText>
      ) : null}
    </FormControl>
  );
};

export default Select;
